import React, { useState, useEffect } from 'react';
import { useSession } from '../../shared/contexts/SessionContext.js';

interface SessionTimeoutModalProps {
  warningMinutes: number; // Minutes before timeout to show warning
}

type ExpiredReason = 'idle' | 'absolute';

const SessionTimeoutModal: React.FC<SessionTimeoutModalProps> = ({ warningMinutes = 5 }) => {
  const { 
    isAuthenticated, 
    isSessionExpired, 
    idleTimeoutMinutes,
    absoluteTimeoutMinutes,
    lastActivityTime,
    sessionStartTime,
    refreshSession, 
    logout 
  } = useSession();
  
  const [showModal, setShowModal] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [isWarning, setIsWarning] = useState(true); // true = warning, false = expired
  const [expiredReason, setExpiredReason] = useState<ExpiredReason>('idle');

  useEffect(() => {
    if (!isAuthenticated || (!lastActivityTime && !sessionStartTime)) return;
    
    const checkTimeRemaining = () => {
      if (isSessionExpired) {
        setIsWarning(false);
        setShowModal(true);
        return;
      }
      
      const now = new Date();

      // Check absolute timeout proximity first (higher priority warning)
      if (sessionStartTime) {
        const sessionElapsed = (now.getTime() - sessionStartTime.getTime()) / (1000 * 60);
        const absoluteRemaining = Math.max(0, absoluteTimeoutMinutes - sessionElapsed);
        if (absoluteRemaining <= warningMinutes && absoluteRemaining > 0) {
          setExpiredReason('absolute');
          setTimeRemaining(Math.floor(absoluteRemaining));
          setIsWarning(true);
          setShowModal(true);
          return;
        }
      }

      // Check idle timeout proximity
      if (lastActivityTime) {
        const idleTime = (now.getTime() - lastActivityTime.getTime()) / (1000 * 60);
        const idleRemaining = Math.max(0, idleTimeoutMinutes - idleTime);
        if (idleRemaining <= warningMinutes && idleRemaining > 0) {
          setExpiredReason('idle');
          setTimeRemaining(Math.floor(idleRemaining));
          setIsWarning(true);
          setShowModal(true);
          return;
        }
      }

      setShowModal(false);
    };
    
    // Check every 15 seconds
    const intervalId = window.setInterval(checkTimeRemaining, 15 * 1000);
    
    // Initial check
    checkTimeRemaining();
    
    return () => {
      window.clearInterval(intervalId);
    };
  }, [isAuthenticated, isSessionExpired, lastActivityTime, sessionStartTime, idleTimeoutMinutes, absoluteTimeoutMinutes, warningMinutes]);

  // Determine expired reason when session expires
  useEffect(() => {
    if (!isSessionExpired || !sessionStartTime) return;
    const now = new Date();
    const sessionElapsed = (now.getTime() - sessionStartTime.getTime()) / (1000 * 60);
    setExpiredReason(sessionElapsed >= absoluteTimeoutMinutes ? 'absolute' : 'idle');
  }, [isSessionExpired, sessionStartTime, absoluteTimeoutMinutes]);
  
  const handleContinue = () => {
    refreshSession();
    setShowModal(false);
  };
  
  const handleLogout = () => {
    logout();
  };
  
  if (!showModal) return null;
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 max-w-md w-full">
        <div className="text-center">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
            {isWarning ? 'Session Timeout Warning' : 'Session Expired'}
          </h3>
          
          <div className="mb-6">
            {isWarning ? (
              expiredReason === 'absolute' ? (
                <p className="text-gray-700 dark:text-gray-300">
                  Your session will reach its <span className="font-bold">maximum duration</span> in{' '}
                  <span className="font-bold">{timeRemaining} minute{timeRemaining !== 1 ? 's' : ''}</span>.
                  <br />
                  Please save your work. You will need to log in again.
                </p>
              ) : (
                <p className="text-gray-700 dark:text-gray-300">
                  Your session will expire in <span className="font-bold">{timeRemaining} minute{timeRemaining !== 1 ? 's' : ''}</span> due to inactivity.
                  <br />
                  Would you like to continue your session?
                </p>
              )
            ) : (
              expiredReason === 'absolute' ? (
                <p className="text-gray-700 dark:text-gray-300">
                  Your session has reached its maximum duration.
                  <br />
                  Please log in again to continue.
                </p>
              ) : (
                <p className="text-gray-700 dark:text-gray-300">
                  Your session has expired due to inactivity.
                  <br />
                  Please log in again to continue.
                </p>
              )
            )}
          </div>
          
          <div className="flex justify-center space-x-4">
            {isWarning && expiredReason === 'idle' ? (
              <>
                <button
                  onClick={handleContinue}
                  className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500"
                >
                  Continue Session
                </button>
                <button
                  onClick={handleLogout}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-400"
                >
                  Logout
                </button>
              </>
            ) : (
              <button
                onClick={handleLogout}
                className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                Log In Again
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SessionTimeoutModal;
